import { AccountInfo } from '@azure/msal-browser';
import { msalService } from './msalService';
import { oidcService } from './oidcService';
import { authLogger } from '../utils/logger';
import { OIDC_ENABLED } from '../config/oidcConfig';

/**
 * Auth Provider Types
 */
export type AuthProviderType = 'msal' | 'oidc';

/**
 * Unified user profile interface that works with both providers
 */
export interface UnifiedUserProfile {
  localAccountId: string;
  homeAccountId: string;
  username: string;
  name: string;
  email: string;
  provider: AuthProviderType;
}

/**
 * Auth Provider Service
 * 
 * Provides a unified interface for authentication that abstracts
 * the underlying provider (MSAL for Azure AD or OIDC for renewmyday.com)
 */
class AuthProviderService {
  private activeProvider: AuthProviderType | null = null;

  constructor() {
    // Try to restore active provider from session storage
    const storedProvider = sessionStorage.getItem('auth_provider') as AuthProviderType | null;
    if (storedProvider && (storedProvider === 'msal' || storedProvider === 'oidc')) {
      this.activeProvider = storedProvider;
      authLogger.debug('Restored auth provider from session:', storedProvider);
    }
  }

  /**
   * Get the currently active auth provider
   */
  getActiveProvider(): AuthProviderType | null {
    return this.activeProvider;
  }

  /**
   * Set the active auth provider
   */
  setActiveProvider(provider: AuthProviderType): void {
    this.activeProvider = provider;
    sessionStorage.setItem('auth_provider', provider);
    authLogger.debug('Set active auth provider:', provider);
  }

  /**
   * Clear the active provider (on logout)
   */
  clearActiveProvider(): void {
    this.activeProvider = null;
    sessionStorage.removeItem('auth_provider');
    authLogger.debug('Cleared active auth provider');
  }

  /**
   * Check if OIDC provider is available
   */
  isOidcAvailable(): boolean {
    return OIDC_ENABLED && oidcService.isEnabled();
  }

  /**
   * Check if user is authenticated with any provider
   */
  async isAuthenticated(): Promise<boolean> {
    // Check MSAL first
    if (msalService.isInitialized()) {
      const msalAccounts = msalService.getAccounts();
      if (msalAccounts.length > 0) {
        this.setActiveProvider('msal');
        return true;
      }
    }

    // Check OIDC
    if (this.isOidcAvailable() && oidcService.isInitialized()) {
      const isOidcAuth = await oidcService.isAuthenticated();
      if (isOidcAuth) {
        this.setActiveProvider('oidc');
        return true;
      }
    }

    return false;
  }

  /**
   * Get access token from the active provider
   */
  async getAccessToken(): Promise<string | null> {
    const provider = await this.determineActiveProvider();

    if (provider === 'msal') {
      return this.getMsalAccessToken();
    } else if (provider === 'oidc') {
      return oidcService.getAccessToken();
    }

    authLogger.warn('No active auth provider for token acquisition');
    return null;
  }

  /**
   * Get access token from MSAL
   */
  private async getMsalAccessToken(): Promise<string | null> {
    const instance = msalService.getInstance();
    const account = msalService.getActiveAccount();
    
    if (!account) {
      authLogger.warn('No active MSAL account');
      return null;
    }

    try {
      const { apiRequest } = await import('../config/authConfig');
      const response = await instance.acquireTokenSilent({
        scopes: apiRequest.scopes,
        account,
      });
      return response.accessToken;
    } catch (error) {
      authLogger.error('MSAL token acquisition failed:', error);
      return null;
    }
  }

  /**
   * Determine which provider is currently active
   */
  private async determineActiveProvider(): Promise<AuthProviderType | null> {
    // Use stored preference if available
    if (this.activeProvider) {
      return this.activeProvider;
    }

    // Check MSAL
    if (msalService.isInitialized()) {
      const msalAccounts = msalService.getAccounts();
      if (msalAccounts.length > 0) {
        this.setActiveProvider('msal');
        return 'msal';
      }
    }

    // Check OIDC
    if (this.isOidcAvailable() && oidcService.isInitialized()) {
      const isOidcAuth = await oidcService.isAuthenticated();
      if (isOidcAuth) {
        this.setActiveProvider('oidc');
        return 'oidc';
      }
    }

    return null;
  }

  /**
   * Get unified user profile from the active provider
   */
  async getUserProfile(): Promise<UnifiedUserProfile | null> {
    const provider = await this.determineActiveProvider();

    if (provider === 'msal') {
      const account = msalService.getActiveAccount();
      if (account) {
        return this.convertMsalAccountToProfile(account);
      }
    } else if (provider === 'oidc') {
      const oidcProfile = oidcService.getUserProfile();
      if (oidcProfile) {
        return {
          ...oidcProfile,
          provider: 'oidc',
        };
      }
    }

    return null;
  }

  /**
   * Convert MSAL AccountInfo to unified profile
   */
  private convertMsalAccountToProfile(account: AccountInfo): UnifiedUserProfile {
    return {
      localAccountId: account.localAccountId,
      homeAccountId: account.homeAccountId || account.localAccountId,
      username: account.username,
      name: account.name || account.username,
      email: account.username,
      provider: 'msal',
    };
  }

  /**
   * Sign in with specified provider
   */
  async signIn(provider: AuthProviderType): Promise<void> {
    authLogger.info('Signing in with provider:', provider);
    
    this.setActiveProvider(provider);

    if (provider === 'msal') {
      const { loginRequest } = await import('../config/authConfig');
      const instance = msalService.getInstance();
      await instance.loginRedirect(loginRequest);
    } else if (provider === 'oidc') {
      await oidcService.signIn();
    } else {
      throw new Error(`Unknown auth provider: ${provider}`);
    }
  }

  /**
   * Sign out from the active provider
   */
  async signOut(): Promise<void> {
    const provider = this.activeProvider;
    authLogger.info('Signing out from provider:', provider);

    this.clearActiveProvider();

    if (provider === 'msal') {
      const instance = msalService.getInstance();
      await instance.logoutRedirect({
        postLogoutRedirectUri: '/',
      });
    } else if (provider === 'oidc') {
      await oidcService.signOut();
    } else {
      // Fallback: redirect to login
      window.location.href = '/login';
    }
  }

  /**
   * Get the account/user object from the active provider
   * Returns MSAL AccountInfo or OIDC user profile
   */
  getAccount(): AccountInfo | ReturnType<typeof oidcService.getUserProfile> | null {
    if (this.activeProvider === 'msal') {
      return msalService.getActiveAccount();
    } else if (this.activeProvider === 'oidc') {
      return oidcService.getUserProfile();
    }
    return null;
  }

  /**
   * Check if a specific provider is initialized
   */
  isProviderInitialized(provider: AuthProviderType): boolean {
    if (provider === 'msal') {
      return msalService.isInitialized();
    } else if (provider === 'oidc') {
      return oidcService.isInitialized();
    }
    return false;
  }
}

// Export singleton instance
export const authProviderService = new AuthProviderService();
